import type { FC } from 'react';
import { Heading, Spacer } from '@chakra-ui/react';

import { TeamMemberRole } from '__generated__/GQL';
import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { InvitationCostReview } from 'features/Team/components/InvitationCostReview';
import { TeamMemberRoleCard } from 'features/Team/components/TeamMemberRoleCard';
import * as Layout from 'layouts/StickyStepperLayout';

import locales from './locales';

type MemberReviewLayoutIdentityBase = {
  firstName: string;
  lastName: string;
  email: string;
};

type MemberReviewLayoutIdentity = MemberReviewLayoutIdentityBase & {
  role: TeamMemberRole;
};

type MemberReviewLayoutIdentityWithDiff = MemberReviewLayoutIdentityBase & {
  currentRole: TeamMemberRole;
  newRole: TeamMemberRole;
};

type MemberReviewLayoutBaseProps = {
  onBackClicked?: () => void;
  onApplyClicked?: () => void;
  onCancelClicked?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  effectMessage?: string;
  confirmLabel: string;
};

type MemberReviewLayoutProps = MemberReviewLayoutBaseProps &
  (
    | {
        showDiff: true;
        identity: MemberReviewLayoutIdentityWithDiff;
      }
    | {
        showDiff?: false;
        identity: MemberReviewLayoutIdentity;
      }
  );

export const MemberReviewLayout: FC<MemberReviewLayoutProps> = (props) => {
  const { confirmLabel, effectMessage, isDisabled, isLoading } = props;

  const handleBackButtonClick = () => props.onBackClicked?.();
  const handleCancelButtonClick = () => props.onCancelClicked?.();
  const handleConfirmButtonClick = () => props.onApplyClicked?.();

  const currentRole = props.showDiff ? props.identity.currentRole : undefined;
  const newRole = props.showDiff ? props.identity.newRole : props.identity.role;

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={handleBackButtonClick} />
        </Layout.HeaderButtonsContainer>
        <Layout.ContentsContainer gap="space-32">
          <Heading size="lg">{locales.title}</Heading>
          {props.showDiff ? (
            <TeamMemberRoleCard
              currentRole={props.identity.currentRole}
              identity={props.identity}
              newRole={newRole}
              showDiff={true}
            />
          ) : (
            <TeamMemberRoleCard
              identity={props.identity}
              role={newRole}
              showDiff={false}
            />
          )}
          <InvitationCostReview
            currentRole={currentRole}
            effectMessage={effectMessage}
            newRole={newRole}
            showDiff={props.showDiff}
          />
        </Layout.ContentsContainer>
        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button
              onClick={handleCancelButtonClick}
              variant="inline-secondary"
            >
              {locales.cancel}
            </Button>
            <Spacer />
            <Button
              isDisabled={isDisabled}
              isLoading={isLoading}
              onClick={handleConfirmButtonClick}
            >
              {confirmLabel}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};
